import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const usePushSubscription = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    console.log('[Push] Checking subscription support:', {
      serviceWorker: 'serviceWorker' in navigator,
      PushManager: 'PushManager' in window,
      Notification: 'Notification' in window,
      permission: 'Notification' in window ? Notification.permission : 'N/A',
    });

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[Push] Push not supported in this browser/environment');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      console.log('[Push] Service worker ready, scope:', registration.scope);
      const subscription = await (registration as any).pushManager.getSubscription();
      console.log('[Push] Existing subscription:', subscription ? 'YES' : 'NONE');
      
      if (subscription) {
        // Browser has a subscription — verify it exists in the DB too
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const subData = JSON.parse(JSON.stringify(subscription));
          const { data: dbRecord } = await supabase
            .from('push_subscriptions' as any)
            .select('id')
            .eq('endpoint', subData.endpoint)
            .maybeSingle();
          
          if (!dbRecord) {
            console.log('[Push] Browser subscription exists but DB record missing — re-saving');
            // Auto-repair: save to DB
            const { error } = await supabase.rpc('upsert_push_subscription' as any, {
              p_user_id: user.id,
              p_endpoint: subData.endpoint,
              p_p256dh: subData.keys.p256dh,
              p_auth: subData.keys.auth,
            });
            if (error) {
              console.error('[Push] Auto-repair DB save failed:', error);
              // Mark as not subscribed so the prompt can re-appear
              setIsSubscribed(false);
              return;
            }
            console.log('[Push] Auto-repair succeeded');
          }
        }
        setIsSubscribed(true);
      } else {
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('[Push] Error checking push subscription:', error);
    }
  };

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const isNativeApp = /AppBuild|wv|WebView|; wv\)/i.test(navigator.userAgent) || 
    (window as any).AppBuild !== undefined ||
    (window as any).webkit?.messageHandlers !== undefined;

  const subscribe = async () => {
    setLoading(true);
    try {
      // In native app wrappers, push is handled natively — just confirm success
      if (isNativeApp) {
        console.log('[Push] Native app detected (UA:', navigator.userAgent, '), skipping web push');
        setIsSubscribed(true);
        toast({
          title: "Notifications enabled",
          description: "You'll receive push notifications through the Mateo app.",
        });
        setLoading(false);
        return true;
      }

      // Check current permission state first
      let permission = Notification.permission;
      console.log('[Push] Current permission state:', permission);
      
      if (permission === 'default') {
        permission = await Notification.requestPermission();
        console.log('[Push] Permission after request:', permission);
      }
      
      if (permission === 'denied') {
        // On Android PWA, guide user to app info settings
        const isAndroid = /android/i.test(navigator.userAgent);
        toast({
          title: "Notifications blocked",
          description: isAndroid 
            ? "Go to your phone's Settings → Apps → Chrome (or Mateo) → Notifications → Enable"
            : "Please enable notifications in your browser settings to receive updates.",
          variant: "destructive",
        });
        setLoading(false);
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || 'BEV7KyL_uR13DZj6R2A8DYzZyn7QV5vTSB-E7nQYAttL_CM0mpFWt-HT5R56UWciyyvXLU_40oQGrPbGkSHhwEM';
      
      if (!vapidPublicKey) {
        console.warn('VAPID public key not configured');
        toast({
          title: "Configuration required",
          description: "Push notifications are not yet configured. Please contact support.",
        });
        setLoading(false);
        return false;
      }

      console.log('[Push] Subscribing with VAPID key:', vapidPublicKey.substring(0, 20) + '...');
      const subscription = await (registration as any).pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      console.log('[Push] Browser subscription created:', subscription.endpoint.substring(0, 60) + '...');

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      console.log('[Push] Saving subscription for user:', user.id);

      // Store subscription in database using service role
      const subscriptionData = JSON.parse(JSON.stringify(subscription));
      
      const { error } = await supabase.rpc('upsert_push_subscription' as any, {
        p_user_id: user.id,
        p_endpoint: subscriptionData.endpoint,
        p_p256dh: subscriptionData.keys.p256dh,
        p_auth: subscriptionData.keys.auth,
      });

      if (error) {
        console.error('[Push] RPC upsert failed:', error);
        // Fallback to direct insert if RPC not available
        const { error: insertError } = await supabase
          .from('push_subscriptions' as any)
          .upsert({
            user_id: user.id,
            endpoint: subscriptionData.endpoint,
            p256dh: subscriptionData.keys.p256dh,
            auth: subscriptionData.keys.auth,
          });
        
        if (insertError) {
          console.error('[Push] Direct upsert also failed:', insertError);
          throw insertError;
        }
        console.log('[Push] Saved via direct upsert (fallback)');
      } else {
        console.log('[Push] Saved via RPC successfully');
      }

      setIsSubscribed(true);
      toast({
        title: "Notifications enabled",
        description: "You'll now receive push notifications for important updates.",
      });
      
      setLoading(false);
      return true;
    } catch (error: any) {
      console.error('Error subscribing to push notifications:', error);
      toast({
        title: "Subscription failed",
        description: error.message || "Could not enable push notifications.",
        variant: "destructive",
      });
      setLoading(false);
      return false;
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await (registration as any).pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        
        // Remove from database
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const subscriptionData = JSON.parse(JSON.stringify(subscription));
          await supabase
            .from('push_subscriptions' as any)
            .delete()
            .eq('user_id', user.id)
            .eq('endpoint', subscriptionData.endpoint);
        }
        
        setIsSubscribed(false);
        toast({
          title: "Notifications disabled",
          description: "You've unsubscribed from push notifications.",
        });
      }
      
      setLoading(false);
    } catch (error: any) {
      console.error('Error unsubscribing from push notifications:', error);
      toast({
        title: "Error",
        description: error.message || "Could not disable notifications.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const isConfigured = true; // VAPID key hardcoded as fallback
  
  return {
    isSubscribed,
    loading,
    subscribe,
    unsubscribe,
    isSupported: 'serviceWorker' in navigator && 'PushManager' in window,
    isConfigured,
  };
};